package com.example.mystybag.common.ui.components.native

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.tween
import androidx.compose.animation.expandVertically
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.takeOrElse
import androidx.compose.ui.text.LinkAnnotation
import androidx.compose.ui.text.LinkInteractionListener
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.TextLinkStyles
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.withLink
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.core.model.ColoredText
import com.example.mystybag.core.model.LinkModel
import com.example.mystybag.core.ui.theme.extraSmall
import com.example.mystybag.core.ui.theme.navItem
import com.example.mystybag.core.views.AppTheme

object Texts {

    @Composable
    fun DisplayLarge(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textAlign: TextAlign? = null, modifier: Modifier = Modifier) {
        val style = AppTheme.current.typography.displayLarge
        Text(
            text        = text,
            modifier    = modifier,
            textAlign   = textAlign,
            overflow    = TextOverflow.Ellipsis,
            maxLines    = maxLines,
            style       = color?.let { style.copy(color = it) } ?: style
        )
    }

    @Composable
    fun DisplayMedium(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textAlign: TextAlign? = null, modifier: Modifier = Modifier) {
        val style = AppTheme.current.typography.displayMedium
        Text(
            text        = text,
            modifier    = modifier,
            textAlign   = textAlign,
            overflow    = TextOverflow.Ellipsis,
            maxLines    = maxLines,
            style       = color?.let { style.copy(color = it) } ?: style
        )
    }

    @Composable
    fun DisplaySmall(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textAlign: TextAlign? = null, modifier: Modifier = Modifier) {
        val style = AppTheme.current.typography.displaySmall
        Text(
            text        = text,
            modifier    = modifier,
            textAlign   = textAlign,
            overflow    = TextOverflow.Ellipsis,
            maxLines    = maxLines,
            style       = color?.let { style.copy(color = it) } ?: style
        )
    }

    @Composable
    fun HeadlineLarge(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textAlign: TextAlign? = null, modifier: Modifier = Modifier, fontWeight: FontWeight? = null) {
        val style = AppTheme.current.typography.headlineLarge
        Text(
            text        = text,
            modifier    = modifier,
            textAlign   = textAlign,
            overflow    = TextOverflow.Ellipsis,
            maxLines    = maxLines,
            fontWeight  = fontWeight,
            style       = color?.let { style.copy(color = it) } ?: style
        )
    }

    @Composable
    fun HeadlineMedium(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textAlign: TextAlign? = null, modifier: Modifier = Modifier, fontWeight: FontWeight? = null) {
        val style = AppTheme.current.typography.headlineMedium
        Text(
            text        = text,
            modifier    = modifier,
            textAlign   = textAlign,
            overflow    = TextOverflow.Ellipsis,
            maxLines    = maxLines,
            fontWeight  = fontWeight,
            style       = color?.let { style.copy(color = it) } ?: style
        )
    }

    @Composable
    fun HeadlineSmall(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textAlign: TextAlign? = null, modifier: Modifier = Modifier) {
        val style = AppTheme.current.typography.headlineSmall
        Text(
            text        = text,
            modifier    = modifier,
            textAlign   = textAlign,
            overflow    = TextOverflow.Ellipsis,
            maxLines    = maxLines,
            style       = color?.let { style.copy(color = it) } ?: style
        )
    }

    @Composable
    fun TitleLarge(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textAlign: TextAlign? = null, modifier: Modifier = Modifier) {
        val style = AppTheme.current.typography.titleLarge
        Text(
            text        = text,
            modifier    = modifier,
            textAlign   = textAlign,
            overflow    = TextOverflow.Ellipsis,
            maxLines    = maxLines,
            style       = color?.let { style.copy(color = it) } ?: style
        )
    }

    @Composable
    fun TitleMedium(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textDecoration: TextDecoration? = null, textAlign: TextAlign? = null, modifier: Modifier = Modifier, fontWeight: FontWeight? = null, onTextLayout: (TextLayoutResult) -> Unit = {}) {
        val style = AppTheme.current.typography.titleMedium
        Text(
            text            = text,
            modifier        = modifier,
            textDecoration  = textDecoration,
            textAlign       = textAlign,
            overflow        = TextOverflow.Ellipsis,
            maxLines        = maxLines,
            fontWeight      = fontWeight,
            style           = color?.let { style.copy(color = it) } ?: style,
            onTextLayout    = onTextLayout
        )
    }

    @Composable
    fun UnderlinedText(
        text: String,
        color: Color? = null,
        maxLines: Int = Int.MAX_VALUE,
        textDecoration: TextDecoration? = null, // ще се игнорира
        textAlign: TextAlign? = null,
        modifier: Modifier = Modifier,
        fontWeight: FontWeight? = null,
        onTextLayout: (TextLayoutResult) -> Unit = {},
        underlineOffset: Dp = 16.dp,
        underlineThickness: Dp = 2.dp,
        underlineColor: Color? = null,
    ) {
        val finalColor = color ?: Color.Unspecified

        Text(
            text = text,
            modifier = modifier.drawBehind {
                val strokeWidth = underlineThickness.toPx()
                val y = size.height + underlineOffset.toPx()

                drawLine(
                    color = underlineColor ?: (finalColor.takeOrElse { Color.Black }),
                    start = Offset(0f, y),
                    end = Offset(size.width, y),
                    strokeWidth = strokeWidth
                )
            },
            color = finalColor,
            maxLines = maxLines,
            textAlign = textAlign,
            fontWeight = fontWeight,
            onTextLayout = onTextLayout,

            // ВАЖНО: махаме стандартното underline
            textDecoration = null
        )
    }

    @Composable
    fun TitleSmall(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textDecoration: TextDecoration? = null, textAlign: TextAlign? = null, modifier: Modifier = Modifier, onTextLayout: (TextLayoutResult) -> Unit = {}) {
        val style = AppTheme.current.typography.titleSmall
        Text(
            text            = text,
            modifier        = modifier,
            textDecoration  = textDecoration,
            textAlign       = textAlign,
            overflow        = TextOverflow.Ellipsis,
            maxLines        = maxLines,
            style           = color?.let { style.copy(color = it) } ?: style,
            onTextLayout    = onTextLayout
        )
    }

    @Composable
    fun BodyLarge(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textDecoration: TextDecoration? = null, textAlign: TextAlign? = null, fontWeight: FontWeight? = null, modifier: Modifier = Modifier, fontStyle: FontStyle? = null) {
        var style = AppTheme.current.typography.bodyLarge
        color?.also {
            style = style.copy(color = it)
        }
        fontStyle?.also {
            style = style.copy(fontStyle = it)
        }
        Text(
            text            = text,
            modifier        = modifier,
            textDecoration  = textDecoration,
            textAlign       = textAlign,
            overflow        = TextOverflow.Ellipsis,
            maxLines        = maxLines,
            fontWeight      = fontWeight,
            style           = style
        )
    }

    @Composable
    fun BodyMedium(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textDecoration: TextDecoration? = null, textAlign: TextAlign? = null, modifier: Modifier = Modifier) {
        val style = AppTheme.current.typography.bodyMedium
        Text(
            text            = text,
            modifier        = modifier,
            textDecoration  = textDecoration,
            textAlign       = textAlign,
            overflow        = TextOverflow.Ellipsis,
            maxLines        = maxLines,
            style           = color?.let { style.copy(color = it) } ?: style
        )
    }

    @Composable
    fun Button(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textAlign: TextAlign? = null, modifier: Modifier = Modifier) {
        val style = AppTheme.current.typography.titleSmall
        Text(
            text        = text,
            modifier    = modifier,
            textAlign   = textAlign,
            overflow    = TextOverflow.Ellipsis,
            maxLines    = maxLines,
            style       = color?.let { style.copy(color = it) } ?: style
        )
    }

    @Composable
    fun BodySmall(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textAlign: TextAlign? = null, modifier: Modifier = Modifier, textDecoration: TextDecoration? = null, fontStyle: FontStyle? = null) {
        var style = AppTheme.current.typography.bodySmall
        color?.also {
            style = style.copy(color = it)
        }
        fontStyle?.also {
            style = style.copy(fontStyle = it)
        }
        Text(
            text            = text,
            modifier        = modifier,
            textAlign       = textAlign,
            overflow        = TextOverflow.Ellipsis,
            maxLines        = maxLines,
            style           = style,
            textDecoration  = textDecoration
        )
    }

    @Composable
    fun Overline(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textAlign: TextAlign? = null, modifier: Modifier = Modifier, uppercased: Boolean = true) {
        val style = AppTheme.current.typography.labelSmall
        Text(
            text        = if (uppercased) text.uppercase() else text,
            modifier    = modifier,
            textAlign   = textAlign,
            overflow    = TextOverflow.Ellipsis,
            maxLines    = maxLines,
            style       = color?.let { style.copy(color = it) } ?: style
        )
    }

    @Composable
    fun ExtraSmall(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textAlign: TextAlign? = null, modifier: Modifier = Modifier) {
        val style = AppTheme.current.typography.extraSmall
        Text(
            text        = text,
            modifier    = modifier,
            textAlign   = textAlign,
            overflow    = TextOverflow.Ellipsis,
            maxLines    = maxLines,
            style       = color?.let { style.copy(color = it) } ?: style
        )
    }

    @Composable
    fun NavigationItem(text: String, color: Color? = null,textAlign: TextAlign? = null, modifier: Modifier = Modifier) {
        val style = AppTheme.current.typography.navItem
        Text(
            text        = text,
            modifier    = modifier,
            textAlign   = textAlign,
            overflow    = TextOverflow.Ellipsis,
            maxLines    = 1,
            style       = color?.let { style.copy(color = it) } ?: style
        )
    }

    @Composable
    fun StrikeThroughText(text: String, style: TextStyle, modifier: Modifier = Modifier, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textAlign: TextAlign? = null) {
        val localStyle = style.copy(
            textDecoration  = TextDecoration.combine(
                mutableListOf(TextDecoration.LineThrough).apply {
                    style.textDecoration?.also {
                        add(it)
                    }
                }
            ),
            color           = color ?: style.color
        )
        Text(
            text        = text,
            modifier    = modifier,
            textAlign   = textAlign,
            overflow    = TextOverflow.Ellipsis,
            maxLines    = maxLines,
            style       = localStyle
        )
    }

    @Composable
    fun Label(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textDecoration: TextDecoration? = null, textAlign: TextAlign? = null, fontWeight: FontWeight? = null, modifier: Modifier = Modifier, fontStyle: FontStyle? = null, textStyle: TextStyle? = null) {
        var style = textStyle ?: AppTheme.current.typography.bodyLarge
        color?.also {
            style = style.copy(color = it)
        }
        fontStyle?.also {
            style = style.copy(fontStyle = it)
        }
        Text(
            text            = text,
            modifier        = modifier,
            textDecoration  = textDecoration,
            textAlign       = textAlign,
            overflow        = TextOverflow.Ellipsis,
            maxLines        = maxLines,
            fontWeight      = fontWeight,
            style           = style
        )
    }

    @Composable
    fun LabelLarge(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textDecoration: TextDecoration? = null, textAlign: TextAlign? = null, fontWeight: FontWeight? = null, modifier: Modifier = Modifier, fontStyle: FontStyle? = null) {
        var style = AppTheme.current.typography.labelLarge
        color?.also {
            style = style.copy(color = it)
        }
        fontStyle?.also {
            style = style.copy(fontStyle = it)
        }
        Text(
            text = text,
            modifier = modifier,
            textDecoration = textDecoration,
            textAlign = textAlign,
            overflow = TextOverflow.Ellipsis,
            maxLines = maxLines,
            fontWeight = fontWeight,
            style = style
        )
    }

    @Composable
    fun LabelMedium(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textDecoration: TextDecoration? = null, textAlign: TextAlign? = null, fontWeight: FontWeight? = null, modifier: Modifier = Modifier, fontStyle: FontStyle? = null) {
        var style = AppTheme.current.typography.labelMedium
        color?.also {
            style = style.copy(color = it)
        }
        fontStyle?.also {
            style = style.copy(fontStyle = it)
        }
        Text(
            text = text,
            modifier = modifier,
            textDecoration = textDecoration,
            textAlign = textAlign,
            overflow = TextOverflow.Ellipsis,
            maxLines = maxLines,
            fontWeight = fontWeight,
            style = style
        )
    }
    @Composable
    fun LabelSmall(text: String, color: Color? = null, maxLines: Int = Int.MAX_VALUE, textDecoration: TextDecoration? = null, textAlign: TextAlign? = null, fontWeight: FontWeight? = null, modifier: Modifier = Modifier, fontStyle: FontStyle? = null) {
        var style = AppTheme.current.typography.labelSmall
        color?.also {
            style = style.copy(color = it)
        }
        fontStyle?.also {
            style = style.copy(fontStyle = it)
        }
        Text(
            text = text,
            modifier = modifier,
            textDecoration = textDecoration,
            textAlign = textAlign,
            overflow = TextOverflow.Ellipsis,
            maxLines = maxLines,
            fontWeight = fontWeight,
            style = style
        )
    }

    private fun formatStringWithSpaces(input: String): String = input.replace(Regex("(\\d)(?=(\\d{3})+(?!\\d))"), "$1 ")

    @Composable
    fun ResizableText(
        text        : String,
        style       : TextStyle,
        color       : Color,
        minSize     : TextUnit,
        stepSize    : TextUnit = 1.sp,
        modifier    : Modifier = Modifier,
    ) {
        val textStyle   = remember { mutableStateOf(style) }
        val readyToDraw = remember { mutableStateOf(true) }
        Text(
            text     = text,
            style    = textStyle.value,
            color    = color,
            maxLines = 1,
            softWrap = false,
            modifier = modifier.drawWithContent {
                if (readyToDraw.value)
                    drawContent()
            },
            onTextLayout = { textLayoutResult ->
                if (textLayoutResult.didOverflowWidth) {
                    val newSize = textStyle.value.fontSize.value - stepSize.value
                    if (newSize >= minSize.value) {
                        textStyle.value = textStyle.value.copy(fontSize = newSize.sp)
                    }
                }
                else
                    readyToDraw.value = true
            }
        )
    }

    @Composable
    fun ColoredTextView(
        texts       : List<ColoredText>,
        modifier    : Modifier          = Modifier,
        textAlign   : TextAlign         = TextAlign.Unspecified,
        textStyle   : TextStyle         = AppTheme.current.typography.bodyLarge
    ) {
        buildAnnotatedString {
            texts.forEach { model ->
                withStyle(
                    style = SpanStyle(
                        color       = model.color,
                        fontSize    = textStyle.fontSize,
                        fontWeight  = textStyle.fontWeight,
                        fontFamily  = textStyle.fontFamily
                    )
                ) {
                    append(model.text)
                }
            }
        }.also { content ->
            Text(
                text     = content,
                modifier = modifier,
                style    = TextStyle(textAlign = textAlign, lineHeight = textStyle.lineHeight)
            )
        }
    }

    @Composable
    fun ClickableTextView(
        modifier        : Modifier          = Modifier,
        texts           : List<LinkModel>   = listOf(),
        textAlign       : TextAlign         = TextAlign.Unspecified,
        textStyle       : TextStyle         = AppTheme.current.typography.bodyLarge,
        onItemClick     : ((Int) -> Unit)?  = null,
        mainTextColor   : Color             = AppTheme.current.typography.bodyLarge.color
    ) {
        buildAnnotatedString {
            texts.forEachIndexed { index, model ->
                if (model.link.isNotEmpty()) {
                    withLink(
                        LinkAnnotation.Clickable(
                            tag     = model.text,
                            styles  = TextLinkStyles(
                                style = SpanStyle(
                                    color           = AppTheme.current.colors.primary,
                                    fontSize        = textStyle.fontSize,
                                    fontWeight      = textStyle.fontWeight,
                                    fontFamily      = textStyle.fontFamily,
                                    textDecoration  = TextDecoration.Underline
                                )
                            ),
                            linkInteractionListener = remember {
                                LinkInteractionListener {
                                    onItemClick?.invoke(index)
                                }
                            }
                        )
                    ) {
                        append(model.text)
                    }
                }
                else {
                    withStyle(
                        style = SpanStyle(
                            color       = mainTextColor,
                            fontSize    = textStyle.fontSize,
                            fontWeight  = textStyle.fontWeight,
                            fontFamily  = textStyle.fontFamily
                        )
                    ) {
                        append(model.text)
                    }
                }
            }
        }.also { content ->
            Text(
                text     = content,
                modifier = modifier,
                style    = TextStyle(textAlign = textAlign, lineHeight = textStyle.lineHeight)
            )
        }
    }

    @Composable
    fun ClickableTextsInTextView(
        modifier        : Modifier          = Modifier,
        mainText        : String            = "",
        texts           : List<LinkModel>   = listOf(),
        textAlign       : TextAlign         = TextAlign.Unspecified,
        textStyle       : TextStyle         = AppTheme.current.typography.bodyLarge,
        onItemClick     : ((Int) -> Unit)?  = null,
        mainTextColor   : Color             = AppTheme.current.typography.bodyLarge.color
    ) {
        buildAnnotatedString {
            withStyle(
                style = SpanStyle(
                    color       = mainTextColor,
                    fontSize    = textStyle.fontSize,
                    fontWeight  = textStyle.fontWeight,
                    fontFamily  = textStyle.fontFamily
                )
            ) {
                append(mainText.substring(0, mainText.indexOf(texts.first().text)))
            }

            texts.forEachIndexed { index, linkModel ->
                withLink(
                    LinkAnnotation.Clickable(
                        tag     = linkModel.text,
                        styles  = TextLinkStyles(
                            style = SpanStyle(
                                color           = AppTheme.current.colors.primary,
                                fontSize        = textStyle.fontSize,
                                fontWeight      = textStyle.fontWeight,
                                fontFamily      = textStyle.fontFamily,
                                textDecoration  = TextDecoration.Underline
                            )
                        ),
                        linkInteractionListener = remember {
                            LinkInteractionListener {
                                onItemClick?.invoke(index)
                            }
                        }
                    )
                ) {
                    append(linkModel.text)
                }

                if (index != texts.lastIndex) {
                    withStyle(
                        style = SpanStyle(
                            color       = mainTextColor,
                            fontSize    = textStyle.fontSize,
                            fontWeight  = textStyle.fontWeight,
                            fontFamily  = textStyle.fontFamily
                        )
                    ) {
                        append(mainText.substring(mainText.indexOf(linkModel.text) + linkModel.text.length, mainText.indexOf(texts[index + 1].text)))
                    }
                }
            }

            withStyle(
                style = SpanStyle(
                    color       = mainTextColor,
                    fontSize    = textStyle.fontSize,
                    fontWeight  = textStyle.fontWeight,
                    fontFamily  = textStyle.fontFamily
                )
            ) {
                append(mainText.substring(mainText.indexOf(texts.last().text) + texts.last().text.length, mainText.length))
            }
        }.let { content ->
            Text(
                text     = content,
                modifier = modifier,
                style    = TextStyle(textAlign = textAlign, lineHeight = textStyle.lineHeight)
            )
        }
    }

    @Composable
    fun ExpandableText(
        modifier            : Modifier      = Modifier,
        style               : TextStyle     = LocalTextStyle.current,
        fontStyle           : FontStyle?    = null,
        text                : String        = "",
        collapsedMaxLine    : Int           = 4,
        readMoreText        : String        = "",
        readMoreStyle       : SpanStyle     = SpanStyle(color = AppTheme.current.colors.primary, fontWeight = FontWeight.W300),
        readLessText        : String        = "",
        readLessStyle       : SpanStyle     = readMoreStyle,
        textAlign           : TextAlign?    = null
    ) {
        var isExpanded by remember { mutableStateOf(false) }
        var clickable by remember { mutableStateOf(false) }
        var lastCharIndex by remember { mutableStateOf(0) }

        Column(modifier) {
            Text(
                modifier        = Modifier
                    .noRippleClickable {
                        if (clickable)
                            isExpanded = !isExpanded
                    }
                    .fillMaxWidth(),
                text            = buildAnnotatedString {
                    if (clickable) {
                        if (isExpanded)
                            append(text.substring(startIndex = 0, endIndex = lastCharIndex))
                        else {
                            append(
                                text.substring(startIndex = 0, endIndex = lastCharIndex)
                                    .dropLast("... ".length + readMoreText.length)
                                    .dropLastWhile { /*Character.isWhitespace(it) ||*/ it == '.' }
                            )
                            append("... ")
                            withStyle(style = readMoreStyle) { append(readMoreText) }
                        }
                    } else
                        append(text)
                },
                maxLines        = if (isExpanded) Int.MAX_VALUE else collapsedMaxLine,
                fontStyle       = fontStyle,
                onTextLayout    = { textLayoutResult ->
                    if (!isExpanded && textLayoutResult.hasVisualOverflow) {
                        clickable       = true
                        lastCharIndex   = textLayoutResult.getLineEnd(collapsedMaxLine - 1)
                    }
                },
                style           = style,
                textAlign       = textAlign
            )

            AnimatedVisibility(
                visible     = isExpanded,
                enter       = expandVertically(animationSpec = tween(100, easing = LinearEasing)),
                exit        = shrinkVertically(animationSpec = tween(100, easing = LinearEasing))
            ) {
                Text(
                    modifier    = Modifier
                        .noRippleClickable {
                            if (clickable)
                                isExpanded = !isExpanded
                        }
                        .fillMaxWidth(),
                    text        = buildAnnotatedString {
                        append(text.substring(startIndex = lastCharIndex, endIndex = text.length - 1))
                        withStyle(style = readLessStyle) { append(" $readLessText") }
                    },
                    maxLines    = Int.MAX_VALUE,
                    fontStyle   = fontStyle,
                    style       = style,
                    textAlign   = textAlign
                )
            }
        }
    }
}